import {
  setCalculationTypesMapsData,
  setDestinationTablesData,
  setFieldResource,
  setInterfaceData,
  setMapDetails,
  setMapsData,
  setSourceFieldValidationData,
  setUnifierRawTableData,
  setUniqueDefinitionsMapsData,
} from "../../../features/mapsSlice";
import { setDisplayErrorSnackBar } from "../../../features/userSlice";
import { MapsService } from "../../../services/api/maps.endpoints";
import store from "../../../store";

async function getInterface(oauthTokenHeader: string) {
  try {
    const response = await MapsService.getInterface(oauthTokenHeader);
    if (!response.status || response.status !== 200) {
      throw new Error();
    } else {
      store.dispatch(setInterfaceData(response?.data?.data));
    }
  } catch (error: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: error?.parsedText || "Unknown error occurred",
        type: "error",
      })
    );
  }
}

async function getDestinationTables(oauthTokenHeader: any) {
  try {
    const response = await MapsService.getDestinationTables(oauthTokenHeader);
    if (!response.status || response.status !== 200) {
      throw new Error();
    } else {
      store.dispatch(setDestinationTablesData(response?.data?.data));
    }
  } catch (error: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: error?.parsedText || "Unknown error occurred",
        type: "error",
      })
    );
  }
}
async function getAllCalculationTypesMaps(oauthTokenHeader: any) {
  try {
    const response = await MapsService.getAllCalculationTypesMaps(
      oauthTokenHeader
    );
    if (!response.status || response.status !== 200) {
      throw new Error();
    } else {
      store.dispatch(setCalculationTypesMapsData(response?.data?.data));
    }
  } catch (error: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: error?.parsedText || "Unknown error occurred",
        type: "error",
      })
    );
  }
}
async function getUnifierUniqueDefinitionMaps(oauthTokenHeader: any) {
  try {
    const response = await MapsService.getUnifierUniqueDefinitionMaps(
      oauthTokenHeader
    );
    if (!response.status || response.status !== 200) {
      throw new Error();
    } else {
      store.dispatch(setUniqueDefinitionsMapsData(response?.data?.data));
    }
  } catch (error: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: error?.parsedText || "Unknown error occurred",
        type: "error",
      })
    );
  }
}

async function getMaps(
  oauthTokenHeader: any,
  selectedInterfaceID: string | null,
  dispatch: any
) {
  if (!selectedInterfaceID) {
    dispatch(
      setDisplayErrorSnackBar({
        message: "Please select an interface.",
        type: "error",
      })
    );
    return;
  }
  try {
    const response = await MapsService.getMapsData(
      oauthTokenHeader,
      selectedInterfaceID
    );
    if (
      !response?.status ||
      (response?.status !== 200 && response?.status !== 204)
    ) {
      throw new Error();
    } else if (response?.status === 204) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "No content available",
          type: "warning",
        })
      );
      store.dispatch(setMapsData([]));
    } else {
      store.dispatch(setMapsData(response?.data?.data));
    }
  } catch (error: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: error?.parsedText || "Unknown error occurred",
        type: "error",
      })
    );
  }
}

async function getMapDetails(oauthTokenHeader: string, mapID: string) {
  try {
    const response = await MapsService.getMapDetails(oauthTokenHeader, mapID);
    if (response.status === 204) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "No Data Found For This Map ID",
          type: "warning",
        })
      );
      return;
    }
    if (!response.status || response.status !== 200) {
      throw new Error();
    } else {
      store.dispatch(setMapDetails(response?.data?.data));
    }
  } catch (error: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: error?.parsedText || "Unknown error occurred",
        type: "error",
      })
    );
  }
}

async function getFieldResource(oauthTokenHeader: string, fieldID: string) {
  try {
    const response = await MapsService.getFieldResource(
      oauthTokenHeader,
      fieldID
    );
    if (response.status === 204) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "No content available for the selected field.",
          type: "warning",
        })
      );
      return;
    } // Debug log
    if (!response.status || response.status !== 200) {
      throw new Error();
    } else {
      store.dispatch(setFieldResource(response?.data?.data));
    }
  } catch (error: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: error?.parsedText || "Unknown error occurred",
        type: "error",
      })
    );
  }
}

async function createMap(oauthTokenHeader: string, data: any) {
  try {
    const response = await MapsService.createMap(oauthTokenHeader, data);
    // Ensure response exists and check for both 200 and 201
    if (!response || !response.status) {
      return { reason: "Failed to create new map." };
    }

    if (response.status === 201) {
      return response;
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (e: any) {
    throw new Error(e?.parsedText?.reason || "Unknown error occurred.");
  }
}

async function editMap(oauthTokenHeader: string, id: string, data: any) {
  try {
    const response = await MapsService.editMap(oauthTokenHeader, id, data);

    if (!response.status || response.status !== 200) {
      const err: any = new Error("Edit map failed");
      err.status = response.status;
      err.parsedText = response.data;
      throw err;
    }

    return response; // ✅ important
  } catch (error: any) {
    if (error?.status === 404) {
      let message = "Map not found (404).";

      if (typeof error?.parsedText === "object" && error?.parsedText.reason) {
        const missing = error.parsedText.reason.missing || [];
        if (missing.length > 0) {
          message = missing
            .map((m: any) => {
              const tables = m.missing_in
                .map((mi: any) => `${mi.raw_data_table} (brand ${mi.brand_id})`)
                .join(", ");
              return `Expression "${m.expression}" missing in: ${tables}`;
            })
            .join("; ");
        }
      } else if (typeof error?.parsedText === "string") {
        message = error.parsedText;
      }

      store.dispatch(
        setDisplayErrorSnackBar({
          message,
          type: "error",
        })
      );
    } else {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "Unknown error occurred",
          type: "error",
        })
      );
    }

    throw error; // ✅ rethrow so handleEditMap can also catch if needed
  }
}

async function validateSourceField(oauthTokenHeader: string, data: any) {
  try {
    const response = await MapsService.validateSourceField(
      oauthTokenHeader,
      data
    );
    if (!response.status || response.status !== 200) {
      throw new Error();
    } else {
      store.dispatch(setSourceFieldValidationData(response?.data?.data));
    }
  } catch (error: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: error?.parsedText || "Unknown error occurred",
        type: "error",
      })
    );
  }
}

async function unifierRawTableData(oauthTokenHeader: any, id: any) {
  try {
    const response = await MapsService.unifierRawTableData(
      oauthTokenHeader,
      id
    );
    if (!response.status || response.status !== 200) {
      throw new Error();
    } else {
      store.dispatch(setUnifierRawTableData(response?.data?.data));
    }
  } catch (error: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: error?.parsedText || "Unknown error occurred",
        type: "error",
      })
    );
  }
}

export const MapsEndpointLogic = {
  getInterface,
  getDestinationTables,
  getMaps,
  getMapDetails,
  getFieldResource,
  getAllCalculationTypesMaps,
  getUnifierUniqueDefinitionMaps,
  createMap,
  editMap,
  validateSourceField,
  unifierRawTableData,
};
